import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BriefcaseBusiness, Plus, Save, Trash2 } from "lucide-react";

const emptyProfile = {
  enabled: false,
  account_type: "creator",
  headline: "",
  about: "",
  categories: [],
  website: "",
  accepting_collaborations: false,
};

const emptyOffering = {
  title: "",
  description: "",
  offering_type: "service",
  fulfillment_mode: "unspecified",
  price_label: "",
  currency: "USD",
  active: true,
  fulfillment_note: "",
};

const fulfillmentOptions = [
  ["unspecified", "Choose how it is fulfilled"],
  ["digital_in_app", "Digital content / access used in HAEVEN"],
  ["web_only_digital", "Digital access used only on the web"],
  ["realtime_1to1", "Live 1-to-1 service"],
  ["realtime_group", "Live group / one-to-many service"],
  ["physical_service", "Physical / in-person service"],
  ["physical_good", "Physical good"],
  ["in_person_event", "In-person event"],
];

export default function CreatorStudioPage() {
  const [profile, setProfile] = useState(emptyProfile);
  const [offerings, setOfferings] = useState([]);
  const [draft, setDraft] = useState(emptyOffering);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/creator/me");
      setProfile({ ...emptyProfile, ...(data.profile || {}), website: data.profile?.website || "" });
      setOfferings(data.offerings || []);
    } catch {
      toast.error("Could not load Creator Studio");
    }
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      const payload = {
        ...profile,
        categories: Array.isArray(profile.categories) ? profile.categories : [],
        website: profile.website?.trim() || null,
      };
      const { data } = await api.put("/creator/me", payload);
      setProfile({ ...emptyProfile, ...data.profile, website: data.profile?.website || "" });
      toast.success("Creator profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const addOffering = async () => {
    if (!draft.title.trim()) return toast.error("Give the offering a title");
    setBusy(true);
    try {
      const { data } = await api.post("/creator/offerings", draft);
      setOfferings((items) => [data, ...items]);
      setDraft(emptyOffering);
      toast.success("Offering added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add offering");
    } finally {
      setBusy(false);
    }
  };

  const removeOffering = async (id) => {
    try {
      await api.delete(`/creator/offerings/${id}`);
      setOfferings((items) => items.filter((item) => item.id !== id));
      toast.success("Offering removed");
    } catch {
      toast.error("Could not remove offering");
    }
  };

  return (
    <div data-testid="creator-studio-page" className="space-y-6">
      <section className="brutal-card bg-accent p-6">
        <div className="flex items-center gap-3 mb-2">
          <BriefcaseBusiness size={24} strokeWidth={2.5} />
          <h1 className="text-3xl">Creator Studio</h1>
        </div>
        <p className="text-sm max-w-2xl text-muted-foreground">
          Turn your HAEVEN identity into a creator, business, or organization presence. Catalog offerings here; checkout stays separate so each platform can enforce the correct payment rules.
        </p>
      </section>

      <section className="brutal-card bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-2xl">Professional profile</h2>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={profile.enabled} onChange={(e) => setProfile({ ...profile, enabled: e.target.checked })} />
            Publicly enabled
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <select aria-label="Account type" className="brutal-input px-3 py-2" value={profile.account_type} onChange={(e) => setProfile({ ...profile, account_type: e.target.value })}>
            <option value="creator">Creator</option>
            <option value="business">Business</option>
            <option value="organization">Organization</option>
          </select>
          <input aria-label="Creator headline" className="brutal-input px-3 py-2" placeholder="Headline" value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} />
          <input aria-label="Creator website" className="brutal-input px-3 py-2" placeholder="Website (optional)" value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} />
          <input
            aria-label="Creator categories"
            className="brutal-input px-3 py-2"
            placeholder="Categories, comma separated"
            value={(profile.categories || []).join(", ")}
            onChange={(e) => setProfile({ ...profile, categories: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
          />
        </div>
        <textarea aria-label="Creator description" className="brutal-input w-full px-3 py-2" rows={4} placeholder="What do you create, offer, or help people accomplish?" value={profile.about} onChange={(e) => setProfile({ ...profile, about: e.target.value })} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={profile.accepting_collaborations} onChange={(e) => setProfile({ ...profile, accepting_collaborations: e.target.checked })} />
          Open to collaborations
        </label>
        <button disabled={busy} onClick={saveProfile} className="brutal-btn bg-primary text-primary-foreground px-4 py-2 flex items-center gap-2 w-fit">
          <Save size={16} /> Save profile
        </button>
      </section>

      <section className="brutal-card bg-card p-5 space-y-4">
        <div>
          <h2 className="text-2xl">Offerings</h2>
          <p className="text-xs text-muted-foreground mt-1">Describe what you offer and how it is fulfilled. HAEVEN uses that distinction to fail closed on ambiguous payment routes rather than accidentally sending a digital purchase through the wrong channel.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input aria-label="Offering title" className="brutal-input px-3 py-2" placeholder="Offering title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <select aria-label="Offering type" className="brutal-input px-3 py-2" value={draft.offering_type} onChange={(e) => setDraft({ ...draft, offering_type: e.target.value })}>
            <option value="service">Service</option>
            <option value="membership">Membership</option>
            <option value="digital_product">Digital product</option>
            <option value="event">Event</option>
            <option value="community">Community</option>
          </select>
          <select aria-label="Offering fulfillment" className="brutal-input px-3 py-2" value={draft.fulfillment_mode} onChange={(e) => setDraft({ ...draft, fulfillment_mode: e.target.value })}>
            {fulfillmentOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input aria-label="Offering price label" className="brutal-input px-3 py-2" placeholder="Price label, e.g. $25/session" value={draft.price_label} onChange={(e) => setDraft({ ...draft, price_label: e.target.value })} />
          <input aria-label="Offering fulfillment note" className="brutal-input px-3 py-2 sm:col-span-2" placeholder="Fulfillment note" value={draft.fulfillment_note} onChange={(e) => setDraft({ ...draft, fulfillment_note: e.target.value })} />
        </div>
        <textarea aria-label="Offering description" className="brutal-input w-full px-3 py-2" rows={3} placeholder="Describe the value someone receives" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        {draft.fulfillment_mode === "unspecified" && (
          <div className="border-2 border-foreground rounded-xl p-3 text-xs bg-secondary">
            Checkout will remain policy-review-only until fulfillment is classified.
          </div>
        )}
        <button disabled={busy || !profile.enabled} onClick={addOffering} className="brutal-btn bg-foreground text-background px-4 py-2 flex items-center gap-2 w-fit disabled:opacity-50">
          <Plus size={16} /> Add offering
        </button>

        <div className="grid md:grid-cols-2 gap-3 pt-2">
          {offerings.map((item) => (
            <article key={item.id} className="brutal-card bg-secondary p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold">{item.offering_type?.replaceAll("_", " ")}</div>
                  <h3 className="text-xl mt-1">{item.title}</h3>
                </div>
                <button aria-label={`Delete ${item.title}`} onClick={() => removeOffering(item.id)} className="brutal-btn bg-card p-2"><Trash2 size={14} /></button>
              </div>
              {item.description && <p className="text-sm mt-2">{item.description}</p>}
              {item.price_label && <div className="font-bold mt-3">{item.price_label}</div>}
              <div className="text-xs text-muted-foreground mt-2">
                {item.fulfillment_mode?.replaceAll("_", " ") || "fulfillment unspecified"} · {item.active ? "Visible" : "Hidden"} · checkout not yet enabled
              </div>
            </article>
          ))}
          {!offerings.length && <div className="text-sm text-muted-foreground">No offerings yet.</div>}
        </div>
      </section>
    </div>
  );
}
